import type { Layer } from "@geosoft/viewer-sdk";
import type { IconName } from "@/design";
import type { UnifiedSceneTreeNode } from "./scenePackage";
import {
  dispatchPanoramaBubbleSelect,
  dispatchPanoramaBubbleStationChange,
  selectPanoramaBubbleLayer,
  selectPanoramaBubbleStation,
  type SceneSelection,
} from "./sceneSelection";

/** Canonical scene-tree node kind for a panorama-bubble project layer. */
export const PANORAMA_BUBBLE_NODE_KIND = "panorama-bubble";

/** Scene-tree node kind for an individual TLS scan station. */
export const PANORAMA_BUBBLE_STATION_NODE_KIND = "panorama-bubble-station";

/** LoR tier for panorama bubble layers per MULTI_SCALE_LOR_ARCHITECTURE.md §LoR7. */
export const PANORAMA_BUBBLE_LOR_TIER = "LoR7";

export const PANORAMA_BUBBLE_DEFAULT_LABEL = "Panoramas (Bubble View)";

export const PANORAMA_BUBBLE_NODE_ICON: IconName = "360";

export interface PanoramaBubbleStationRef {
  readonly id: string;
  readonly name?: string;
}

const STATION_NODE_PREFIX = "panorama-station:";

export function isPanoramaBubbleLayer(
  layer: Layer | null | undefined,
): boolean {
  return layer?.kind === PANORAMA_BUBBLE_NODE_KIND;
}

export function isPanoramaBubbleNode(node: UnifiedSceneTreeNode): boolean {
  return node.kind === PANORAMA_BUBBLE_NODE_KIND;
}

export function isPanoramaBubbleStationNode(
  node: UnifiedSceneTreeNode,
): boolean {
  return node.kind === PANORAMA_BUBBLE_STATION_NODE_KIND;
}

export function panoramaStationNodeId(
  layerId: string,
  stationId: string,
): string {
  return `${STATION_NODE_PREFIX}${layerId}:${stationId}`;
}

export function parsePanoramaStationNodeId(
  nodeId: string,
): { readonly layerId: string; readonly stationId: string } | null {
  if (!nodeId.startsWith(STATION_NODE_PREFIX)) return null;
  const rest = nodeId.slice(STATION_NODE_PREFIX.length);
  const separator = rest.indexOf(":");
  if (separator <= 0) return null;
  const layerId = rest.slice(0, separator);
  const stationId = rest.slice(separator + 1);
  if (!layerId || !stationId) return null;
  return { layerId, stationId };
}

export function readPanoramaBubbleLayerId(
  node: UnifiedSceneTreeNode,
): string | null {
  if (isPanoramaBubbleNode(node)) {
    return node.sceneItemIds[0] ?? null;
  }
  if (isPanoramaBubbleStationNode(node)) {
    const fromAttrs = node.attributes?.panoramaBubbleLayerId;
    if (typeof fromAttrs === "string" && fromAttrs.length > 0) return fromAttrs;
    return parsePanoramaStationNodeId(node.id)?.layerId ?? null;
  }
  return null;
}

export function readPanoramaBubbleStationId(
  node: UnifiedSceneTreeNode,
): string | null {
  if (!isPanoramaBubbleStationNode(node)) return null;
  const fromAttrs = node.attributes?.panoramaBubbleStationId;
  if (typeof fromAttrs === "string" && fromAttrs.length > 0) return fromAttrs;
  return parsePanoramaStationNodeId(node.id)?.stationId ?? null;
}

export function readPanoramaBubbleStationsFromLayer(
  layer: Layer,
): readonly PanoramaBubbleStationRef[] {
  const style = layer.style;
  if (!style || typeof style !== "object") return [];
  const raw = (style as Record<string, unknown>).panoramaBubbleStations;
  if (!Array.isArray(raw)) return [];
  const stations: PanoramaBubbleStationRef[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    if (typeof record.id !== "string" || record.id.length === 0) continue;
    stations.push({
      id: record.id,
      name: typeof record.name === "string" ? record.name : undefined,
    });
  }
  return stations;
}

export function buildPanoramaBubbleStationNode(
  layerId: string,
  station: PanoramaBubbleStationRef,
): UnifiedSceneTreeNode {
  return {
    id: panoramaStationNodeId(layerId, station.id),
    label: station.name ?? station.id,
    kind: PANORAMA_BUBBLE_STATION_NODE_KIND,
    sceneItemIds: [],
    geometryRefs: [],
    attributes: {
      panoramaBubbleLayerId: layerId,
      panoramaBubbleStationId: station.id,
    },
    readiness: [],
    integrationStates: [],
    candidateFacts: [],
    intelligenceLevel: PANORAMA_BUBBLE_LOR_TIER,
    children: [],
  };
}

export function buildPanoramaBubbleLayerNode(
  layer: Layer,
  stations: readonly PanoramaBubbleStationRef[],
): UnifiedSceneTreeNode {
  return {
    id: `layer:${layer.id}`,
    label: layer.label ?? PANORAMA_BUBBLE_DEFAULT_LABEL,
    kind: PANORAMA_BUBBLE_NODE_KIND,
    sceneItemIds: [layer.id],
    geometryRefs: [],
    readiness: readStringArray(layer.style?.readiness),
    integrationStates: [],
    candidateFacts: [],
    intelligenceLevel: PANORAMA_BUBBLE_LOR_TIER,
    children: stations.map((station) =>
      buildPanoramaBubbleStationNode(layer.id, station),
    ),
  };
}

/**
 * Walk the unified scene tree and attach station children to every
 * `panorama-bubble` layer node using the supplied station index.
 */
export function enrichSceneTreeWithPanoramaBubbles(
  tree: UnifiedSceneTreeNode,
  layers: readonly Layer[],
  stationsByLayerId: Readonly<
    Record<string, readonly PanoramaBubbleStationRef[]>
  >,
): UnifiedSceneTreeNode {
  const layerById = new Map(layers.map((layer) => [layer.id, layer]));

  const visit = (node: UnifiedSceneTreeNode): UnifiedSceneTreeNode => {
    const children = node.children.map(visit);
    const layerId =
      node.sceneItemIds[0] ??
      (node.kind === PANORAMA_BUBBLE_NODE_KIND ? readPanoramaBubbleLayerId(node) : null);
    const layer = layerId ? layerById.get(layerId) : undefined;
    const isBubbleLayer =
      node.kind === PANORAMA_BUBBLE_NODE_KIND ||
      (Boolean(layer) &&
        isPanoramaBubbleLayer(layer) &&
        node.kind !== "group" &&
        node.kind !== "root" &&
        node.kind !== "scene");

    if (!isBubbleLayer || !layerId || !layer) {
      return children === node.children ? node : { ...node, children };
    }

    const stations =
      stationsByLayerId[layerId] ??
      readPanoramaBubbleStationsFromLayer(layer);
    if (stations.length === 0) {
      return { ...node, kind: PANORAMA_BUBBLE_NODE_KIND, children, intelligenceLevel: PANORAMA_BUBBLE_LOR_TIER };
    }

    return buildPanoramaBubbleLayerNode(layer, stations);
  };

  return visit(tree);
}

/**
 * Resolve a scene-tree node click into a panorama-bubble selection and
 * dispatch the matching viewer bus event. Returns `null` when the node
 * is not part of the panorama-bubble taxonomy.
 */
export function applyPanoramaBubbleTreeSelection(
  node: UnifiedSceneTreeNode,
  layer: Layer | null,
  source: SceneSelection["source"] = "scene-tree",
): SceneSelection | null {
  if (isPanoramaBubbleStationNode(node)) {
    const layerId = readPanoramaBubbleLayerId(node);
    const stationId = readPanoramaBubbleStationId(node);
    if (!layerId || !stationId) return null;
    dispatchPanoramaBubbleStationChange({ layerId, stationId, source });
    return selectPanoramaBubbleStation(layerId, stationId, { source });
  }

  const layerId =
    layer?.id ??
    (isPanoramaBubbleNode(node) ? readPanoramaBubbleLayerId(node) : null);
  if (!layerId || (!isPanoramaBubbleNode(node) && !isPanoramaBubbleLayer(layer))) {
    return null;
  }

  const firstStationId = node.children[0]
    ? (readPanoramaBubbleStationId(node.children[0]) ?? undefined)
    : undefined;
  dispatchPanoramaBubbleSelect({
    layerId,
    layerKind: "panorama-bubble",
    stationId: firstStationId,
    source,
  });
  return selectPanoramaBubbleLayer(layerId, {
    stationId: firstStationId,
    source,
  });
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}
